// ─── src/hooks/useGew.ts ─────────────────────────────────────────────────────

import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  deleteGewParticipant, deleteGewPartner, deleteGewSponsor,
  getGewParticipants, getGewParticipantStats,
  getGewPartners, getGewPartnerStats,
  getGewSponsors, getGewSponsorStats,
  type GewParticipantParams, type GewSimpleParams,
} from "../api/gew";

// ── Participants ─────────────────────────────────────────────────────────────

export const gewParticipantKeys = {
  all:   ["gewParticipants"] as const,
  list:  (p: GewParticipantParams) => [...gewParticipantKeys.all, "list", p] as const,
  stats: () => [...gewParticipantKeys.all, "stats"] as const,
};

export const useGewParticipants = (params: GewParticipantParams) =>
  useQuery({
    queryKey: gewParticipantKeys.list(params),
    queryFn:  () => getGewParticipants(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

export const useGewParticipantStats = () =>
  useQuery({
    queryKey: gewParticipantKeys.stats(),
    queryFn: getGewParticipantStats,
    staleTime: 60_000,
  });

export const useDeleteGewParticipant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteGewParticipant,
    onSuccess: () => qc.invalidateQueries({ queryKey: gewParticipantKeys.all }),
  });
};

// ── Partners ─────────────────────────────────────────────────────────────────

export const gewPartnerKeys = {
  all:   ["gewPartners"] as const,
  list:  (p: GewSimpleParams) => [...gewPartnerKeys.all, "list", p] as const,
  stats: () => [...gewPartnerKeys.all, "stats"] as const,
};

export const useGewPartners = (params: GewSimpleParams) =>
  useQuery({
    queryKey: gewPartnerKeys.list(params),
    queryFn:  () => getGewPartners(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

export const useGewPartnerStats = () =>
  useQuery({
    queryKey: gewPartnerKeys.stats(),
    queryFn: getGewPartnerStats,
    staleTime: 60_000,
  });

export const useDeleteGewPartner = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteGewPartner,
    onSuccess: () => qc.invalidateQueries({ queryKey: gewPartnerKeys.all }),
  });
};

// ── Sponsors ─────────────────────────────────────────────────────────────────

export const gewSponsorKeys = {
  all:   ["gewSponsors"] as const,
  list:  (p: GewSimpleParams) => [...gewSponsorKeys.all, "list", p] as const,
  stats: () => [...gewSponsorKeys.all, "stats"] as const,
};

export const useGewSponsors = (params: GewSimpleParams) =>
  useQuery({
    queryKey: gewSponsorKeys.list(params),
    queryFn:  () => getGewSponsors(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

export const useGewSponsorStats = () =>
  useQuery({
    queryKey: gewSponsorKeys.stats(),
    queryFn: getGewSponsorStats,
    staleTime: 60_000,
  });

export const useDeleteGewSponsor = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteGewSponsor,
    onSuccess: () => qc.invalidateQueries({ queryKey: gewSponsorKeys.all }),
  });
};
